import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ContactPickerField, SelectedContact } from '@extension/context/content/components/shared';
import BTTimePicker from '@extension/context/content/components/bt-bulk-dashboard/time-and-date/bt-time-picker';
import {
  backendApi,
  MyBridgeSchedule,
  MyBridgeScheduleRecurrence,
} from '@common/reducers/backend-api/backend-api';
import { Loader2, Plus, Clock, Pencil, Trash2, Play, Check, AlertCircle, Send } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmDialog } from '@common/components/use-confirm-dialog';
import { namespace } from '@common-config/constants';
import { useBridgeChatNames } from './useBridgeChatNames';
import { muted, text } from './agent-inspect-shared';

/**
 * TIME TRIGGERS — an agent's server-side wake-up schedule(s), edited in the agent
 * dialog with the same deterministic model the backend fires on (agent-schedule:
 * structured recurrence → precomputed nextRunAt). Edit-only (like the knowledge
 * files manager): a new agent is saved first, then time triggers are added, so
 * every schedule has a real agentId to bind to. Each add/edit/delete is an
 * immediate mutation (RTK invalidates 'MyBridge', so the list refreshes).
 */

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const browserTz = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Jerusalem';
  } catch {
    return 'Asia/Jerusalem';
  }
};

const pad = (n: number) => String(n).padStart(2, '0');
const recToTime = (r: MyBridgeScheduleRecurrence) => `${pad(r.hour)}:${pad(r.minute)}`;

interface DraftSchedule {
  id?: string; // present = editing an existing schedule
  label: string;
  freq: 'hourly' | 'daily' | 'weekly' | 'monthly';
  time: string; // HH:mm — for hourly, the minute is the minute past each hour and the hour is the interval's anchor
  /** hourly only: every N hours. */
  interval: number;
  weekdays: number[];
  monthday: number;
  timezone: string;
  prompt: string;
  sourceChatId: string;
  sourceMeta: SelectedContact[];
  destChatId: string;
  destMeta: SelectedContact[];
  enabled: boolean;
}

const emptyDraft = (): DraftSchedule => ({
  label: '',
  freq: 'daily',
  time: '09:00',
  interval: 1,
  weekdays: [new Date().getDay()],
  monthday: 1,
  timezone: browserTz(),
  prompt: '',
  sourceChatId: '',
  sourceMeta: [],
  destChatId: '',
  destMeta: [],
  enabled: true,
});

const scheduleToDraft = (s: MyBridgeSchedule, nameOf: (id: string) => string): DraftSchedule => {
  const r = s.recurrence;
  const idToMeta = (id?: string): SelectedContact[] =>
    id ? [{ id, name: nameOf(id) }] : [];
  return {
    id: s._id,
    label: s.label ?? '',
    freq: r.freq,
    time: recToTime(r),
    interval: r.interval ?? 1,
    weekdays: r.weekdays ?? [new Date().getDay()],
    monthday: r.monthday ?? 1,
    timezone: r.timezone || browserTz(),
    prompt: s.prompt ?? '',
    sourceChatId: s.sourceChatId ?? '',
    sourceMeta: idToMeta(s.sourceChatId),
    destChatId: s.destChatId ?? '',
    destMeta: idToMeta(s.destChatId),
    enabled: s.enabled !== false,
  };
};

const draftToRecurrence = (d: DraftSchedule): MyBridgeScheduleRecurrence => {
  const [h, m] = d.time.split(':').map((x) => parseInt(x, 10));
  const rec: MyBridgeScheduleRecurrence = { freq: d.freq, hour: h || 0, minute: m || 0, timezone: d.timezone };
  if (d.freq === 'hourly') rec.interval = Math.min(Math.max(Math.round(d.interval) || 1, 1), 24);
  if (d.freq === 'weekly') rec.weekdays = d.weekdays.length ? [...d.weekdays].sort() : [new Date().getDay()];
  if (d.freq === 'monthly') rec.monthday = d.monthday;
  return rec;
};

interface Props {
  agentId: string;
  schedules: MyBridgeSchedule[];
}

export const TimeTriggersEditor: React.FC<Props> = ({ agentId, schedules }) => {
  const { t } = useTranslation(namespace);
  const { confirm, dialog } = useConfirmDialog();
  const [draft, setDraft] = useState<DraftSchedule | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [createSchedule, { isLoading: creating }] = backendApi.useCreateMyBridgeScheduleMutation();
  const [updateSchedule, { isLoading: updating }] = backendApi.useUpdateMyBridgeScheduleMutation();
  const [deleteSchedule] = backendApi.useDeleteMyBridgeScheduleMutation();
  // A second instance of the update hook for the row toggle, so it doesn't spin the editor's Save.
  const [patchSchedule] = backendApi.useUpdateMyBridgeScheduleMutation();
  const [runNow] = backendApi.useRunMyBridgeScheduleNowMutation();
  const saving = creating || updating;

  // Resolve a chatId → its human-readable chat name (e.g. "old test") so triggers
  // show WHERE they run and send, not a raw group id. Shared with the agent dialog.
  const { nameOf } = useBridgeChatNames();
  // Per-row "Run now" state: 'running' | 'ok' | 'err' keyed by schedule id.
  const [runState, setRunState] = useState<Record<string, 'running' | 'ok' | 'err'>>({});
  // The 4s "clear the ok/err badge" timers, cleared on unmount — the editor lives
  // in a dialog the user can close mid-run, and a fired timer would then set
  // state on an unmounted component.
  const clearTimers = useRef<number[]>([]);
  useEffect(() => () => { clearTimers.current.forEach(window.clearTimeout); }, []);

  const doRunNow = async (s: MyBridgeSchedule) => {
    setRunState((p) => ({ ...p, [s._id]: 'running' }));
    try {
      await runNow({ scheduleId: s._id }).unwrap();
      setRunState((p) => ({ ...p, [s._id]: 'ok' }));
    } catch {
      setRunState((p) => ({ ...p, [s._id]: 'err' }));
    }
    clearTimers.current.push(
      window.setTimeout(() => setRunState((p) => { const n = { ...p }; delete n[s._id]; return n; }), 4000),
    );
  };

  const mine = useMemo(
    () => schedules.filter((s) => s.agentId === agentId),
    [schedules, agentId],
  );

  const summary = (s: MyBridgeSchedule): string => {
    const r = s.recurrence;
    const time = recToTime(r);
    if (r.freq === 'hourly') {
      const every = r.interval ?? 1;
      // Every hour reads by the MINUTE past the hour; every N hours needs the
      // anchor time too, or "every 3 hours" says nothing about which three.
      return every > 1
        ? t('myBridge.triggers.summaryHourlyInterval', { interval: every, time })
        : t('myBridge.triggers.summaryHourly', { minute: String(r.minute).padStart(2, '0') });
    }
    if (r.freq === 'daily') return t('myBridge.triggers.summaryDaily', { time });
    if (r.freq === 'weekly') {
      const days = (r.weekdays ?? []).map((d) => t(`myBridge.triggers.weekdayShort.${WEEKDAY_KEYS[d]}`)).join(', ');
      return t('myBridge.triggers.summaryWeekly', { days, time });
    }
    return t('myBridge.triggers.summaryMonthly', { day: r.monthday ?? 1, time });
  };

  const startAdd = () => { setError(null); setDraft(emptyDraft()); };
  const startEdit = (s: MyBridgeSchedule) => { setError(null); setDraft(scheduleToDraft(s, nameOf)); };

  const save = async () => {
    if (!draft) return;
    setError(null);
    if (!draft.prompt.trim()) { setError(t('myBridge.triggers.errorPrompt')); return; }
    if (!draft.sourceChatId) { setError(t('myBridge.triggers.errorSource')); return; }
    const recurrence = draftToRecurrence(draft);
    try {
      if (draft.id) {
        await updateSchedule({
          id: draft.id,
          label: draft.label.trim(),
          recurrence,
          prompt: draft.prompt.trim(),
          sourceChatId: draft.sourceChatId,
          destChatId: draft.destChatId || '',
          enabled: draft.enabled,
        }).unwrap();
      } else {
        await createSchedule({
          agentId,
          label: draft.label.trim(),
          recurrence,
          prompt: draft.prompt.trim(),
          sourceChatId: draft.sourceChatId,
          destChatId: draft.destChatId || undefined,
          enabled: draft.enabled,
        }).unwrap();
      }
      setDraft(null);
    } catch {
      setError(t('myBridge.triggers.errorSave'));
    }
  };

  const toggleEnabled = async (s: MyBridgeSchedule) => {
    try { await patchSchedule({ id: s._id, enabled: s.enabled === false }).unwrap(); } catch { /* the row stays authoritative */ }
  };
  const remove = async (s: MyBridgeSchedule) => {
    if (!(await confirm({ content: t('myBridge.triggers.deleteConfirm') }))) return;
    try { await deleteSchedule({ id: s._id }).unwrap(); } catch { /* the row stays authoritative */ }
  };

  // Single-select helpers for the source/dest chat pickers.
  const pickOne = (setId: (id: string) => void, setMeta: (m: SelectedContact[]) => void) => ({
    onContactSelected: (c: SelectedContact) => { setId(c.id); setMeta([c]); },
    onContactRemoved: () => { setId(''); setMeta([]); },
  });

  return (
    <div className="space-y-3">
      {dialog}
      {/* Existing triggers */}
      {mine.length > 0 && (
        <div className="space-y-2">
          {mine.map((s) => (
            <div
              key={s._id}
              className="flex items-center gap-2 rounded-lg border px-3 py-2"
              style={{ borderColor: 'var(--color-border)', opacity: s.enabled === false ? 0.55 : 1 }}
              data-testid="time-trigger-row"
            >
              <Clock className="h-4 w-4 shrink-0" style={muted} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" style={text} dir="auto">
                  {s.label || summary(s)}
                </div>
                <div className="text-xs truncate" style={muted}>
                  {summary(s)}
                </div>
                <div className="text-xs truncate mt-0.5 flex items-center gap-1" style={muted}>
                  <Send className="h-3 w-3 shrink-0 rtl:rotate-180" style={{ color: 'var(--cta-button-bg)' }} />
                  <span style={text}>
                    {s.destChatId
                      ? t('myBridge.triggers.sendsTo', { name: nameOf(s.destChatId) })
                      : t('myBridge.triggers.sendsToSource')}
                  </span>
                </div>
                {s.agentStopped === true && (
                  <div className="text-xs mt-0.5" style={muted} data-testid="time-trigger-agent-paused">
                    {t('myBridge.triggers.agentPausedHint')}
                  </div>
                )}
              </div>
              <Checkbox
                checked={s.enabled !== false}
                onCheckedChange={() => toggleEnabled(s)}
                aria-label={t('myBridge.triggers.enabledLabel')}
              />
              {/* Run now — fire this trigger immediately (real activation), no waiting. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => doRunNow(s)}
                    disabled={runState[s._id] === 'running'}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border"
                    style={{ borderColor: 'var(--color-border)', color: 'var(--cta-button-bg)' }}
                    data-testid="time-trigger-run-now"
                  >
                    {runState[s._id] === 'running' ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : runState[s._id] === 'ok' ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : runState[s._id] === 'err' ? (
                      <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                    ) : (
                      <Play className="h-3.5 w-3.5" />
                    )}
                    {t('myBridge.triggers.runNow')}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('myBridge.triggers.runNowHint')}</TooltipContent>
              </Tooltip>
              <button type="button" onClick={() => startEdit(s)} className="p-1 rounded hover:bg-black/5" aria-label={t('myBridge.edit')} data-testid="time-trigger-edit">
                <Pencil className="h-3.5 w-3.5" style={muted} />
              </button>
              <button type="button" onClick={() => void remove(s)} className="p-1 rounded hover:bg-black/5" aria-label={t('myBridge.delete')} data-testid="time-trigger-delete">
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Draft editor (add or edit) */}
      {draft ? (
        <div className="space-y-3 rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }} data-testid="time-trigger-editor">
          <Input
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            placeholder={t('myBridge.triggers.labelPlaceholder')}
            dir="auto"
            data-testid="time-trigger-label"
          />
          {/* Frequency */}
          <div className="flex flex-wrap items-center gap-2">
            {(['hourly', 'daily', 'weekly', 'monthly'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setDraft({ ...draft, freq: f })}
                aria-pressed={draft.freq === f}
                className="px-3 py-1 text-sm rounded-full border transition-colors"
                style={{
                  borderColor: draft.freq === f ? 'var(--cta-button-bg)' : 'var(--color-border)',
                  background: draft.freq === f ? 'var(--cta-button-bg)' : 'transparent',
                  color: draft.freq === f ? 'var(--cta-button-text,#fff)' : 'var(--color-text)',
                }}
                data-testid={`trigger-freq-${f}`}
              >
                {t(`myBridge.triggers.freq.${f}`)}
              </button>
            ))}
            <div className="ms-auto">
              <BTTimePicker value={draft.time} onChange={(v) => setDraft({ ...draft, time: v })} />
            </div>
          </div>

          {/* Hourly: how many hours between runs. 1 = every hour, and then the
              picked time contributes only its minute. */}
          {draft.freq === 'hourly' && (
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {t('myBridge.triggers.intervalLabel')}
              </span>
              <Input
                type="number"
                min={1}
                max={24}
                value={String(draft.interval)}
                onChange={(e) =>
                  setDraft({ ...draft, interval: Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), 24) })
                }
                className="w-20"
                data-testid="trigger-hourly-interval"
              />
            </div>
          )}

          {/* Weekly: weekday toggles */}
          {draft.freq === 'weekly' && (
            <div className="flex flex-wrap gap-1">
              {WEEKDAY_KEYS.map((k, idx) => {
                const on = draft.weekdays.includes(idx);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        weekdays: on ? draft.weekdays.filter((d) => d !== idx) : [...draft.weekdays, idx],
                      })
                    }
                    aria-pressed={on}
                    className="w-9 h-8 text-xs rounded-md border transition-colors"
                    style={{
                      borderColor: on ? 'var(--cta-button-bg)' : 'var(--color-border)',
                      background: on ? 'var(--cta-button-bg)' : 'transparent',
                      color: on ? 'var(--cta-button-text,#fff)' : 'var(--color-text)',
                    }}
                  >
                    {t(`myBridge.triggers.weekdayShort.${k}`)}
                  </button>
                );
              })}
            </div>
          )}

          {/* Monthly: day of month */}
          {draft.freq === 'monthly' && (
            <div className="flex items-center gap-2">
              <span className="text-sm" style={muted}>{t('myBridge.triggers.dayOfMonth')}</span>
              <Input
                type="number" min={1} max={31}
                value={draft.monthday}
                onChange={(e) => setDraft({ ...draft, monthday: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })}
                className="w-20"
              />
            </div>
          )}

          {/* What to do */}
          <Textarea
            value={draft.prompt}
            onChange={(e) => setDraft({ ...draft, prompt: e.target.value })}
            placeholder={t('myBridge.triggers.promptPlaceholder')}
            rows={2}
            dir="auto"
            className="resize-y"
            data-testid="time-trigger-prompt"
          />

          {/* The trigger's chat (source): results go here unless "Send to" is set */}
          <div className="space-y-1">
            <span className="text-xs font-medium" style={muted}>{t('myBridge.triggers.readFrom')}</span>
            <ContactPickerField
              collapsible
              value={draft.sourceChatId ? [draft.sourceChatId] : []}
              selectedContacts={draft.sourceMeta}
              height={260}
              searchPlaceholder={t('myBridge.form.chatsSearchPlaceholder')}
              {...pickOne((id) => setDraft((d) => (d ? { ...d, sourceChatId: id } : d)), (m) => setDraft((d) => (d ? { ...d, sourceMeta: m } : d)))}
            />
          </div>

          {/* Send to (dest) — optional; empty = reply in the source chat */}
          <div className="space-y-1">
            <span className="text-xs font-medium" style={muted}>{t('myBridge.triggers.sendTo')}</span>
            <ContactPickerField
              collapsible
              value={draft.destChatId ? [draft.destChatId] : []}
              selectedContacts={draft.destMeta}
              height={260}
              searchPlaceholder={t('myBridge.form.chatsSearchPlaceholder')}
              {...pickOne((id) => setDraft((d) => (d ? { ...d, destChatId: id } : d)), (m) => setDraft((d) => (d ? { ...d, destMeta: m } : d)))}
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setDraft(null)} className="h-9">
              {t('myBridge.triggers.cancel')}
            </Button>
            <Button type="button" onClick={save} disabled={saving} className="h-9" style={{ background: 'var(--cta-button-bg)', color: 'var(--cta-button-text,#fff)' }} data-testid="time-trigger-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {t('myBridge.triggers.saveTrigger')}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={startAdd}
          className="flex items-center gap-2 text-sm font-medium"
          style={{ color: 'var(--cta-button-bg)' }}
          data-testid="time-trigger-add"
        >
          <Plus className="h-4 w-4" /> {t('myBridge.triggers.addTrigger')}
        </button>
      )}
    </div>
  );
};
